import { useEffect, useState } from "react";
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { FileText, Trash2, Upload } from "lucide-react";
import { PROJECT_FILE_KINDS, PROJECT_FILE_LABEL, ROLE_RANK, paths, type ProjectFile } from "@clevis/shared-types";
import { Card, CardTitle, Select, cn } from "@clevis/shared-ui";
import { db, storage } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";

// A project's files: contracts, plans, elevations, bids, photos. Anyone in
// the office adds them; the uploader or a manager removes one.

type Row = ProjectFile & { id: string };
type Kind = (typeof PROJECT_FILE_KINDS)[number];
const MAX = 20 * 1024 * 1024;
const size = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function ProjectFiles({ tenantId, projectId }: { tenantId: string; projectId: string }) {
  const { user, role } = useAuth();
  const isManager = !!role && ROLE_RANK[role] >= ROLE_RANK.manager;
  const [rows, setRows] = useState<Row[]>([]);
  const [kind, setKind] = useState<Kind>("plans");
  const [filter, setFilter] = useState<"" | Kind>("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(
    () => onSnapshot(query(collection(db, paths.tenantProjectFiles(tenantId, projectId)), orderBy("createdAt", "desc")), (s) => setRows(s.docs.map((d) => ({ id: d.id, ...(d.data() as ProjectFile) }))), () => setRows([])),
    [tenantId, projectId],
  );

  async function upload(files: FileList) {
    if (!user) return;
    setError("");
    for (const f of [...files]) {
      if (f.size > MAX) {
        setError(`${f.name} is over 20 MB.`);
        continue;
      }
      setBusy(`Uploading ${f.name}…`);
      try {
        const path = paths.projectFileStorage(tenantId, projectId, `${Date.now()}-${f.name.replace(/[^\w.\- ]+/g, "_").slice(0, 120)}`);
        await uploadBytes(storageRef(storage, path), f, { contentType: f.type || "application/octet-stream" });
        const row: ProjectFile = {
          name: f.name.slice(0, 200),
          kind,
          storagePath: path,
          size: f.size,
          contentType: f.type,
          uploadedByUid: user.uid,
          uploadedByName: user.displayName || user.email || "",
          createdAt: Date.now(),
        };
        await addDoc(collection(db, paths.tenantProjectFiles(tenantId, projectId)), row);
      } catch (err) {
        setError((err as Error).message);
      }
    }
    setBusy("");
  }
  async function open(r: Row) {
    try {
      window.open(await getDownloadURL(storageRef(storage, r.storagePath)), "_blank", "noopener");
    } catch (err) {
      setError((err as Error).message);
    }
  }
  async function remove(r: Row) {
    try {
      await deleteDoc(doc(db, paths.tenantProjectFiles(tenantId, projectId), r.id));
      await deleteObject(storageRef(storage, r.storagePath)).catch(() => undefined);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const shown = rows.filter((r) => !filter || r.kind === filter);
  const kinds = [...new Set(rows.map((r) => r.kind))];
  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
        <CardTitle className="mb-0">Files ({rows.length})</CardTitle>
        <div className="flex items-center gap-1.5">
          {kinds.length > 1 ? (
            <Select className="w-32 py-0.5 text-xs" value={filter} aria-label="Show" onChange={(e) => setFilter(e.target.value as typeof filter)}>
              <option value="">All files</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {PROJECT_FILE_LABEL[k]}
                </option>
              ))}
            </Select>
          ) : null}
          <Select className="w-32 py-0.5 text-xs" value={kind} aria-label="Upload as" onChange={(e) => setKind(e.target.value as Kind)}>
            {PROJECT_FILE_KINDS.map((k) => (
              <option key={k} value={k}>
                {PROJECT_FILE_LABEL[k]}
              </option>
            ))}
          </Select>
          <label className={cn("inline-flex cursor-pointer items-center gap-1 rounded-lg border border-steel/30 bg-white px-2.5 py-1 text-xs text-ink hover:border-steel", busy && "pointer-events-none opacity-60")}>
            <Upload size={13} /> Upload
            <input type="file" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) void upload(e.target.files); e.target.value = ""; }} />
          </label>
        </div>
      </div>
      {busy ? <p className="px-4 pt-1 text-xs text-steel">{busy}</p> : null}
      {error ? <p className="px-4 pt-1 text-xs text-danger">{error}</p> : null}
      <div className="mt-2">
        {shown.map((r) => (
          <div key={r.id} className="flex items-center gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0">
            <FileText size={14} className="shrink-0 text-steel" />
            <button className="min-w-0 flex-1 truncate text-left text-ink hover:text-brandorange-dark hover:underline" onClick={() => void open(r)}>
              {r.name}
            </button>
            <span className="shrink-0 rounded bg-steel/10 px-1.5 text-[10px] text-steel">{PROJECT_FILE_LABEL[r.kind]}</span>
            <span className="hidden shrink-0 text-xs text-steel sm:inline">
              {size(r.size)} · {r.uploadedByName || "—"} · {new Date(r.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
            {r.uploadedByUid === user?.uid || isManager ? (
              <button className="shrink-0 text-steel/60 hover:text-danger" onClick={() => void remove(r)} aria-label={`Remove ${r.name}`}>
                <Trash2 size={13} />
              </button>
            ) : null}
          </div>
        ))}
        {rows.length === 0 ? <p className="px-4 pb-3 text-xs text-steel">Contracts, plans and elevations for this project go here.</p> : null}
      </div>
    </Card>
  );
}
